/**
 * OrganizationsPage — Phase 3.
 *
 * Lists the organizations the current user belongs to. Provides a "create
 * organization" affordance when the backend allows it (the request will
 * 403 cleanly if not permitted; we show a toast on failure).
 *
 * Reused primitives ONLY: existing Card, Button, Dialog, Input, Label,
 * Badge, sonner toast, lucide icons, react-router. No new design language.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Building2, Plus, ChevronRight, Users, Mail } from 'lucide-react';

import { Button } from '../components/ui/button';
import { PageLoader } from '@/components/ui/page-loader';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';

import { useOrganizations } from '../context/OrganizationContext';
import {
  createOrganization,
  createManagementWebsite,
  listMyPendingInvitations,
  type PendingInvitation,
} from '../services/organizationApi';

function normalizeSlug(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function readUserTypeFromStorage(): string | null {
  try {
    const raw = localStorage.getItem('user') || sessionStorage.getItem('user');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.user_type || null;
  } catch {
    return null;
  }
}

const OrganizationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { organizations, loading, refresh } = useOrganizations();
  const isSuperAdmin = readUserTypeFromStorage() === 'super-admin';

  // The management-company workspace is for org owners/admins (and super-admin).
  // Scoped Members (= artist managers) and direct-grant managers don't belong
  // here — kick them back to the dashboard.
  useEffect(() => {
    if (loading) return;
    if (isSuperAdmin) return;
    const hasAdminRole = organizations.some(
      (o) => o.myRole === 'owner' || o.myRole === 'admin',
    );
    if (!hasAdminRole) navigate('/', { replace: true });
  }, [loading, isSuperAdmin, organizations, navigate]);

  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  // Management website option (super-admin only). The website reuses the
  // company slug and is provisioned through POST /api/organizations/:id/website.
  const [createWebsite, setCreateWebsite] = useState(true);

  // Phase 4 — pending invitations addressed to the current user.
  // Tokens are NOT returned by /api/invitations/mine (only the hash is
  // stored server-side). The banner is informational; the user accepts
  // by clicking the share link the inviter sent them.
  const [pendingInvites, setPendingInvites] = useState<PendingInvitation[]>([]);

  useEffect(() => {
    let cancelled = false;
    listMyPendingInvitations()
      .then((rows) => {
        if (!cancelled) setPendingInvites(rows);
      })
      .catch(() => {
        // Non-fatal — the banner is purely additive.
      });
    return () => {
      cancelled = true;
    };
    // Re-fetch when org list changes (e.g. after an accept elsewhere).
  }, [organizations.length]);

  const onNameChange = (v: string) => {
    setName(v);
    if (!slugTouched) setSlug(normalizeSlug(v));
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isSuperAdmin) {
      toast.error('Only super-admin can create management companies');
      return;
    }
    if (name.trim().length < 2) {
      toast.error('Management company name must be at least 2 characters');
      return;
    }
    if (!/^[a-z0-9-]{2,}$/.test(slug)) {
      toast.error('Slug must be lowercase letters, numbers, or hyphens');
      return;
    }
    setSubmitting(true);
    try {
      const org = await createOrganization({
        name: name.trim(),
        slug,
        description: description.trim() || undefined,
      });
      if (createWebsite && org?._id) {
        try {
          await createManagementWebsite(org._id, { slug });
          toast.success('Management company created with its website');
        } catch (webErr: any) {
          toast.warning(
            webErr?.response?.data?.message
              || 'Company created, but its website could not be provisioned — use the "Create website" button on the company page.',
          );
        }
      } else {
        toast.success('Management company created');
      }
      setCreateOpen(false);
      setName('');
      setSlug('');
      setDescription('');
      setSlugTouched(false);
      setCreateWebsite(true);
      await refresh();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to create management company';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4 lg:p-8">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Building2 className="w-6 h-6 text-primary" />
            Management Companies
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Agencies, labels, and teams you collaborate with. Switching to an
            allocated artist uses the same domain selector you already know.
          </p>
        </div>

        {isSuperAdmin && (
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2">
                <Plus className="w-4 h-4" /> New Management Company
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Create Management Company</DialogTitle>
                <DialogDescription>
                  You will become the owner. You can invite members and link
                  artists after creation.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="org-name">Name</Label>
                  <Input
                    id="org-name"
                    value={name}
                    onChange={(e) => onNameChange(e.target.value)}
                    placeholder="e.g. Stellar Records"
                    required
                    minLength={2}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="org-slug">Slug</Label>
                  <Input
                    id="org-slug"
                    value={slug}
                    onChange={(e) => {
                      setSlug(normalizeSlug(e.target.value));
                      setSlugTouched(true);
                    }}
                    placeholder="stellar-records"
                    required
                    pattern="[a-z0-9-]{2,}"
                  />
                  <p className="text-xs text-muted-foreground">
                    Lowercase letters, numbers, hyphens. Used in URLs.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="org-desc">Description (optional)</Label>
                  <Textarea
                    id="org-desc"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Short description shown to members"
                    rows={3}
                  />
                </div>
                <div className="flex items-start gap-3 rounded-lg border border-border p-3">
                  <Switch
                    id="org-website"
                    checked={createWebsite}
                    onCheckedChange={setCreateWebsite}
                  />
                  <div className="space-y-0.5">
                    <Label htmlFor="org-website" className="cursor-pointer">
                      Also create a management website
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      A website for the company itself (Artist Roster, Testimonials,
                      Our Clients, Team), using the slug
                      <span className="font-mono"> {slug || 'company-slug'}</span>.
                      Provisioned and owned by you as super admin.
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setCreateOpen(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" loading={submitting}>
                    Create
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {pendingInvites.length > 0 && (
        <Card className="mb-6 border-amber-200 bg-amber-50/60">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-amber-900">
                  {pendingInvites.length === 1
                    ? 'You have 1 pending invitation'
                    : `You have ${pendingInvites.length} pending invitations`}
                </h3>
                <p className="text-xs text-amber-800/80 mt-1">
                  Check your email for the invitation link, then open it to
                  accept and join. Each link can only be used once.
                </p>
                <ul className="mt-3 space-y-1 text-xs text-amber-900">
                  {pendingInvites.map((inv) => (
                    <li key={inv._id} className="flex items-center gap-2">
                      <Badge variant="outline" className="capitalize bg-white">
                        {inv.invitedRole}
                      </Badge>
                      <span className="font-mono">{inv.email}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {loading && organizations.length === 0 && (
        <PageLoader label="Loading management companies…" />
      )}

      {!loading && organizations.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
            <h3 className="text-base font-semibold text-foreground mb-1">
              You are not part of any management company
            </h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              Management companies let agencies and labels manage multiple
              artists under one roof. {isSuperAdmin
                ? 'Create one or accept an invitation to get started.'
                : 'Accept an invitation to get started.'}{' '}
              Your solo artist site is unaffected either way.
            </p>
          </CardContent>
        </Card>
      )}

      {organizations.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2">
          {organizations.map((org) => (
            <Card
              key={org._id}
              className="cursor-pointer hover:border-primary/40 transition-colors"
              onClick={() => navigate(`/organizations/${org._id}`)}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-base font-semibold flex items-center gap-2 min-w-0">
                    <span className="truncate">{org.name}</span>
                  </CardTitle>
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 mt-1" />
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="text-xs text-muted-foreground font-mono mb-2">
                  /{org.slug}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {org.myRole && (
                    <Badge variant="secondary" className="capitalize">
                      <Users className="w-3 h-3 mr-1" />
                      {org.myRole}
                    </Badge>
                  )}
                  {org.status && org.status !== 'active' && (
                    <Badge variant="destructive" className="capitalize">
                      {org.status}
                    </Badge>
                  )}
                </div>
                {org.description && (
                  <p className="text-sm text-muted-foreground mt-3 line-clamp-2">
                    {org.description}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrganizationsPage;
